import { DocumentType, IssuerType } from "@dune/types";
import {
	Button,
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
	StyledSelect,
} from "@dune/ui";
import {
	documentTypes,
	fileTypeAndIssuerMapping,
	issuerTypes,
} from "@dune/vars";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { useUpdateFileTypeAndIssuerMutation } from "../../../api/mutations/files.mutations";
import { useGetFileWithMetadata } from "../../../api/queries/files.queries";

const zFileTypeAndIssuerForm = z.object({
	fileType: z.enum(documentTypes),
	issuerType: z.enum(issuerTypes).or(z.literal("")),
});

type FileTypeAndIssuerForm = z.infer<typeof zFileTypeAndIssuerForm>;

type FileTypeAndIssuerSelectProps = {
	fileId: string;
};

export const FileTypeAndIssuerSelect = ({
	fileId,
}: FileTypeAndIssuerSelectProps) => {
	const {
		reset,
		watch,
		setValue,
		formState: { isDirty },
	} = useForm<FileTypeAndIssuerForm>({
		resolver: zodResolver(zFileTypeAndIssuerForm),
	});

	const { data: fileWithMetadata } = useGetFileWithMetadata(fileId);

	const {
		mutate: updateFileTypeAndIssuer,
		isPending: isUpdatingFileTypeAndIssuer,
	} = useUpdateFileTypeAndIssuerMutation(fileId);

	useEffect(() => {
		fileWithMetadata &&
			reset({
				fileType: fileWithMetadata?.fileData?.type || "OTHER",
				issuerType: fileWithMetadata?.fileData?.issuer || "",
			});
	}, [fileWithMetadata, reset]);

	const { fileType: formFileType, issuerType: formIssuerType } = watch();

	return (
		<div>
			<StyledSelect
				label="Type de document"
				options={Object.entries(fileTypeAndIssuerMapping).flatMap(
					([type, value]) => {
						if ("label" in value) {
							return {
								label: value.label,
								value: `${type}:`,
							};
						}

						return Object.entries(value).map(([issuer, issuerValue]) => ({
							label: issuerValue.label,
							value: `${type}:${issuer}`,
						}));
					},
				)}
				value={`${formFileType}:${formIssuerType}`}
				onValueChange={(value) => {
					const [type, issuer] = value.split(":");
					setValue("fileType", type as DocumentType, { shouldDirty: true });
					setValue("issuerType", issuer as IssuerType, {
						shouldDirty: true,
					});
				}}
				isLoading={isUpdatingFileTypeAndIssuer}
			/>
			{isDirty && (
				<Dialog>
					<DialogTrigger asChild>
						<Button variant="outline">Valider</Button>
					</DialogTrigger>
					<DialogContent className="flex flex-col gap-0 overflow-y-visible p-0 sm:max-w-lg [&>button:last-child]:top-3.5">
						<DialogHeader className="contents space-y-0 text-left">
							<DialogTitle className="border-b px-6 py-4 text-base">
								Êtes-vous sûr de vouloir changer le type de document ?
							</DialogTitle>
						</DialogHeader>
						<div className="px-6 py-4">
							<p>
								En changeant le type d'un document, vous perdrez les métadonnées
								actuelles. Continuer ?
							</p>
						</div>
						<DialogFooter className="border-t px-6 py-4">
							<DialogClose asChild>
								<Button type="button" variant="outline">
									Annuler
								</Button>
							</DialogClose>
							<DialogClose asChild>
								<Button
									type="button"
									onClick={() =>
										updateFileTypeAndIssuer({
											fileType: formFileType,
											issuerType:
												formIssuerType === "" ? undefined : formIssuerType,
										})
									}
								>
									Valider
								</Button>
							</DialogClose>
						</DialogFooter>
					</DialogContent>
				</Dialog>
			)}
		</div>
	);
};
